import ArticleIcon from '@mui/icons-material/Article';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LanguageIcon from '@mui/icons-material/Language';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CommonHelper } from 'src/utils/commonHelper';
import SetupHeaderCard from 'src/components/setup/setup-header-card.component';

export const defaultCollection = { id: 0, name: '', slug: '', description: '', sort_order: '', is_public: true, is_enabled: true };
export const defaultArticle = { id: 0, collection_id: '', title: '', slug: '', content: '', status: 'draft' };

export const kbRoutes = {
  kb: '/kb-info',
  collections: '/kb-collections',
  articles: '/kb-articles',
};

export const toPathSlug = (value) => {
  const slug = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || 'brand';
};

export const sanitizeKBHtml = (html = '') =>
  String(html)
    .replace(/<\s*(script|style|iframe|object|embed)[^>]*>.*?<\s*\/\s*\1\s*>/gis, '')
    .replace(/<\s*\/?\s*(script|style|iframe|object|embed)[^>]*>/gis, '')
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/href\s*=\s*("|')\s*javascript:[^"']*\1/gi, 'href="#"');

export const htmlToPreviewText = (html = '') =>
  String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const getHelpBaseUrl = () => `${window.location.origin}/knowledge-base/${CommonHelper.GetCurrentBrandId() || ''}`;

export function DomainSummary({ helpBaseUrl, onCopy, disabled = false }) {
  const { t: translate } = useTranslation();

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <LanguageIcon color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">
              {translate('kb_setup_page.public_kb_url', { defaultValue: 'Public knowledge base URL' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {helpBaseUrl}
            </Typography>
          </Box>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          disabled={disabled}
          onClick={onCopy}
        >
          {translate('common.copy', { defaultValue: 'Copy' })}
        </Button>
      </Stack>
    </Paper>
  );
}

export function KBSetupShell({ activeView, title, headerAction, children }) {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>{title || translate('kb_setup_page.title', { defaultValue: 'Knowledge Base Setup' })}</title>
      </Helmet>
      <Box sx={{ height: 'calc(100vh - 70px)', minHeight: 0, overflow: 'hidden', pb: 2 }}>
        <Stack
          spacing={2.5}
          sx={{
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            px: 0.75,
            mx: 'auto',
            pb: { xs: 10, md: 6 },
            scrollbarGutter: 'stable both-edges',
          }}
        >
          <SetupHeaderCard
            icon={<MenuBookRoundedIcon />}
            title={translate('kb_setup_page.title', { defaultValue: 'Knowledge Base Setup' })}
            subtitle={translate('kb_setup_page.subtitle', {
              defaultValue: 'Build collections, publish articles, and share the public Brand2Chat knowledge base URL.',
            })}
            action={headerAction}
          />

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, flexShrink: 0 }}>
            <Stack spacing={2.5}>
            <Tabs value={activeView} onChange={(_event, value) => navigate(kbRoutes[value])}>
              <Tab icon={<LanguageIcon />} iconPosition="start" label={translate('kb_setup_page.kb_tab', { defaultValue: 'KB' })} value="kb" />
              <Tab icon={<CollectionsBookmarkIcon />} iconPosition="start" label={translate('kb_setup_page.collections_tab', { defaultValue: 'Collections' })} value="collections" />
              <Tab icon={<ArticleIcon />} iconPosition="start" label={translate('kb_setup_page.articles_tab', { defaultValue: 'Articles' })} value="articles" />
            </Tabs>

            {children}
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </>
  );
}
